import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { CartIcon } from "../../components/ActionIcons";
import "./RelatedProducts.css";
import { IMAGE_COMING_SOON } from "../../imageComingSoon";

// Up to 3 cards stack vertically in the sidebar with no slider; with a
// 4th item onward the up/down arrows appear and it becomes a vertical
// slider stepping one card at a time.
const VISIBLE_COUNT = 3;

// The item's tagged "Compatible Accessories and Related Items" (tixaru
// Items page), pre-resolved server-side (title/image/price/route) by
// catalogSync.js -- unlike FrequentlyBoughtTogether.jsx, this never
// touches Firestore itself, so it renders immediately with no loading
// state. Sits in the sidebar column beside the buy box (see hmi-fbt-col
// in SevenInchesPage.jsx), same compact card language as that column's
// other content.
const RelatedProducts = ({ products = [] }) => {
  const [startIndex, setStartIndex] = useState(0);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  if (!products || products.length === 0) return null;

  const maxStartIndex = Math.max(0, products.length - VISIBLE_COUNT);
  // Clamp rather than trust state: if the products list ever shrinks while
  // mounted, a stale startIndex past the end would slice to an empty view.
  const safeStartIndex = Math.min(startIndex, maxStartIndex);
  const canGoPrev = safeStartIndex > 0;
  const canGoNext = safeStartIndex < maxStartIndex;
  const visibleProducts = products.slice(safeStartIndex, safeStartIndex + VISIBLE_COUNT);
  const showArrows = products.length > VISIBLE_COUNT;

  const goPrev = () => setStartIndex((i) => Math.max(0, i - 1));
  const goNext = () => setStartIndex((i) => Math.min(maxStartIndex, i + 1));

  return (
    <div className="frequently-bought-sections related-vertical">
      <h3 className="frequently-bought-title">Related Products</h3>
      <div className="frequently-bought-rows">
        {showArrows && (
          <button
            type="button"
            className="fbt-arrow fbt-arrow-prev"
            onClick={goPrev}
            disabled={!canGoPrev}
            aria-label="Show previous related products"
          >
            ‹
          </button>
        )}

        <div className="frequently-bought-grid">
          {visibleProducts.map((product, index) => {
            const cardKey = safeStartIndex + index;
            const displayName = product.title || product.partNo || "Product";
            return (
              <div
                key={cardKey}
                className="frequently-bought-card"
                onClick={() => product.route && navigate(product.route)}
              >
                <div className="fbt-image-container">
                  <img
                    src={product.image || IMAGE_COMING_SOON}
                    alt={displayName}
                    className="fbt-image"
                    onError={(e) => {
                      e.currentTarget.src = IMAGE_COMING_SOON;
                    }}
                  />
                </div>

                <div className="fbt-info">
                  <h4 className="fbt-label" title={displayName}>{displayName}</h4>

                  {product.price != null && (
                    <div className="fbt-price">₹{parseFloat(product.price).toFixed(2)}</div>
                  )}

                  <button
                    className="fbt-add-btn"
                    disabled={product.price == null}
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart({
                        partNo: product.partNo || displayName,
                        title: displayName,
                        image: product.image,
                        quantity: 1,
                        price: parseFloat(product.price) || 0,
                      });
                    }}
                  >
                    <CartIcon size={12} />
                    Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {showArrows && (
          <button
            type="button"
            className="fbt-arrow fbt-arrow-next"
            onClick={goNext}
            disabled={!canGoNext}
            aria-label="Show more related products"
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
};

export default RelatedProducts;
